import { Link } from '@tanstack/react-router';
import type { Task } from '@shared/types';
import {
  DUE_CLASSES,
  PRIORITY_CLASSES,
  PRIORITY_LABELS,
  cn,
  formatDate,
  dueStatus,
} from '@/lib/format';
import { AvatarGroup, LabelChip } from './ui';

/**
 * Dense table view for scanning many tasks at once.
 *
 * Horizontal scrolling is contained here rather than on the page, and the
 * header stays put — a 300-row project is normal in this data.
 */
export function TaskTable({
  tasks,
  onToggleDone,
}: {
  tasks: Task[];
  onToggleDone: (task: Task) => void;
}) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead className="sticky top-0 z-10">
        <tr className="surface-raised text-left">
          <Th className="w-9" />
          <Th className="w-20">ID</Th>
          <Th className="min-w-64">Titel</Th>
          <Th className="w-28">Fällig</Th>
          <Th className="w-24">Priorität</Th>
          <Th className="min-w-32">Labels</Th>
          <Th className="w-24">Zugewiesen</Th>
          <Th className="w-24">Geändert</Th>
        </tr>
      </thead>
      <tbody className="divide-y line surface-card">
        {tasks.map((task) => {
          const status = dueStatus(task.dueDate, task.done);
          return (
            <tr
              key={task.id}
              className={cn('transition-colors hover:bg-[var(--surface-hover)]', task.done && 'opacity-55')}
            >
              <Td>
                <input
                  type="checkbox"
                  checked={task.done}
                  onChange={() => onToggleDone(task)}
                  className="size-4 cursor-pointer rounded accent-brand-500"
                  aria-label={`${task.title} umschalten`}
                />
              </Td>
              <Td className="font-mono text-[11px] text-faint">{task.identifier}</Td>
              <Td>
                <Link
                  to="/tasks/$taskId"
                  params={{ taskId: task.id }}
                  className={cn(
                    'text-strong hover:text-brand-500 hover:underline',
                    task.done && 'line-through',
                  )}
                >
                  {task.title}
                </Link>
              </Td>
              <Td className={cn('whitespace-nowrap text-xs', DUE_CLASSES[status])}>
                {formatDate(task.dueDate) || '—'}
              </Td>
              <Td>
                {task.priority > 0 ? (
                  <span className={cn('text-xs font-medium', PRIORITY_CLASSES[task.priority])}>
                    {PRIORITY_LABELS[task.priority]}
                  </span>
                ) : (
                  <span className="text-xs text-faint">—</span>
                )}
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {task.labels.map((l) => (
                    <LabelChip key={l.id} label={l} size="sm" />
                  ))}
                </div>
              </Td>
              <Td>
                <AvatarGroup users={task.assignees} max={2} />
              </Td>
              <Td className="whitespace-nowrap text-xs text-muted">{formatDate(task.updated)}</Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'border-b line px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2 align-middle', className)}>{children}</td>;
}
